// MCHD Room Scheduler - Floor Management Hook
// React Query hooks for floor management operations
// Created: 09-10-2025 13:26:21

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import {
  createFloor,
  bulkCreateFloors,
  checkFloorAvailability,
} from '../../api/endpoints/floors';

// =============================================================================
// QUERY KEYS
// =============================================================================

export const floorQueryKeys = {
  all: ['floors'] as const,
  lists: () => [...floorQueryKeys.all, 'list'] as const,
  details: () => [...floorQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...floorQueryKeys.details(), id] as const,
  availability: (buildingId: string, floorNumber: number) => 
    [...floorQueryKeys.all, 'availability', buildingId, floorNumber] as const,
};

// =============================================================================
// FLOOR CREATION HOOKS
// =============================================================================

/**
 * Hook to create a single floor
 * Created: 09-10-2025 13:26:21
 * @returns Mutation for creating a floor
 */
export function useCreateFloor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createFloor,
    onSuccess: () => {
      // Invalidate buildings query to refresh tree view
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      queryClient.invalidateQueries({ queryKey: floorQueryKeys.all });
      
      notifications.show({
        title: 'Success',
        message: 'Floor created successfully',
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.error?.message || 'Failed to create floor',
        color: 'red',
      });
    },
  });
}

/**
 * Hook to bulk create floors
 * Created: 09-10-2025 13:26:21
 * @returns Mutation for bulk creating floors
 */
export function useBulkCreateFloors() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: bulkCreateFloors,
    onSuccess: (response) => {
      // Invalidate buildings query to refresh tree view
      queryClient.invalidateQueries({ queryKey: ['buildings'] });
      queryClient.invalidateQueries({ queryKey: floorQueryKeys.all });
      
      const count = response.data?.length || 0;
      notifications.show({
        title: 'Success',
        message: `${count} floor(s) created successfully`,
        color: 'green',
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: 'Error',
        message: error.error?.message || 'Failed to create floors',
        color: 'red',
      });
    },
  });
}

// =============================================================================
// FLOOR AVAILABILITY HOOK
// =============================================================================

/**
 * Hook to check floor number availability
 * Created: 09-10-2025 13:26:21
 * @param buildingId - Building ID
 * @param floorNumber - Floor number to check
 * @returns Query result with availability data
 */
export function useCheckFloorAvailability(buildingId: string, floorNumber: number) {
  return useQuery({
    queryKey: floorQueryKeys.availability(buildingId, floorNumber),
    queryFn: async () => {
      const response = await checkFloorAvailability(buildingId, floorNumber);
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to check availability');
      }
      return response.data;
    },
    enabled: !!buildingId && floorNumber > 0 && floorNumber < 1000,
    staleTime: 30000, // 30 seconds
    gcTime: 60000, // 1 minute
    retry: 1,
  });
}

